import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCanUseBenefits } from '@/hooks/useCanUseBenefits'
import { useClubBenefitPrograms } from '@/services/myBenefits'

import { ClubBenefitsModal } from './ClubBenefitsModal'

type Props = {
  clubId: number | null
  clubName: string
}

/**
 * The club's DUPR+ mark, for its own header.
 *
 * Metadata, weighted as metadata. It sits with the club's address and says the same kind of thing
 * about the club — a titled panel here would read as an advertisement wedged into the page rather
 * than a fact about it.
 *
 * Translucent white rather than the listing badge's tinted fill: this ground is the app's blue
 * gradient, and a 10% blue wash on blue would leave it barely legible.
 *
 * Carries NO eligibility tone, unlike the listing badge. Dimming earns its ambiguity when the eye
 * is sorting one club against another; on a single club's page there is nothing to sort, and the
 * dialog behind this says per-program what the member can actually use.
 *
 * Mirrors `ClubBenefitPill.tsx` in the mobile app.
 */
export const ClubBenefitHeaderPill = ({ clubId, clubName }: Props) => {
  const { t } = useTranslation()
  const benefitsEnabled = useCanUseBenefits()
  // The flag is passed to the query rather than checked after it: a user outside the rollout should
  // not be asking every club they open what it takes part in.
  const { data } = useClubBenefitPrograms(clubId, benefitsEnabled)
  const [open, setOpen] = useState(false)

  // Nothing for a club in no program, and nothing while loading — a header that reflows a beat
  // after it draws is worse than one that arrives complete. Same for a user the benefits feature is
  // not on for, whose query never ran.
  if (!data?.length || clubId === null) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('benefits.clubBenefitsTitle')}
        className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white transition-colors hover:bg-white/30"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3 w-3"
          aria-hidden="true"
        >
          <path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
        {t('benefits.clubBenefitBadge')}
      </button>
      <ClubBenefitsModal open={open} onClose={() => setOpen(false)} clubId={clubId} clubName={clubName} />
    </>
  )
}
